// View Component: Özellikler bölümü
import React from "react";
import TiltCard from "../UI/TiltCard";
import { FeatureData } from "../../models/AppData";
import { useIntersectionObserver } from "../../hooks/useLandingPage";

const Features = () => {
  const [sectionRef, isVisible] = useIntersectionObserver({ threshold: 0.2 });

  const sectionStyles = {
    padding: "var(--spacing-8xl) 0 var(--spacing-9xl) 0",
    background:
      "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 80%, rgba(240, 253, 250, 0.3) 100%)",
    position: "relative",
  };

  const containerStyles = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 var(--spacing-lg)",
    position: "relative",
    zIndex: 2,
  };

  const headerStyles = {
    textAlign: "center",
    marginBottom: "var(--spacing-6xl)",
  };

  const titleStyles = {
    fontFamily: "var(--font-secondary)",
    fontSize: "40px",
    fontWeight: "700",
    color: "var(--gray-900)",
    marginBottom: "var(--spacing-lg)",
  };

  const subtitleStyles = {
    fontSize: "18px",
    color: "var(--gray-600)",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: "1.6",
  };

  const gridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "var(--spacing-3xl)",
    marginTop: "var(--spacing-5xl)",
  };

  const getColorVariant = (color) => {
    const colorMap = {
      teal: "var(--teal-600)",
      orange: "var(--orange-500)",
      purple: "var(--purple-600)",
      blue: "var(--blue-600)",
    };
    return colorMap[color] || "var(--color-primary)";
  };

  const FeatureCard = ({ feature, index }) => {
    const colorVariant = getColorVariant(feature.color);

    const cardStyles = {
      height: "100%",
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      opacity: isVisible ? 1 : 0,
      transition: `all 600ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 150}ms`,
    };

    const iconContainerStyles = {
      width: "80px",
      height: "80px",
      borderRadius: "24px",
      background: `linear-gradient(135deg, ${colorVariant}, ${colorVariant}CC)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "var(--spacing-2xl)",
      boxShadow: `0 8px 24px ${colorVariant}20`,
      position: "relative",
    };

    const iconStyles = {
      fontSize: "36px",
      filter: "brightness(1.2)",
    };

    const featureTitleStyles = {
      fontSize: "20px",
      fontWeight: "700",
      color: "var(--gray-900)",
      marginBottom: "var(--spacing-sm)",
      fontFamily: "var(--font-secondary)",
      lineHeight: "1.3",
    };

    const featureDescriptionStyles = {
      fontSize: "15px",
      color: "var(--gray-600)",
      lineHeight: "1.7",
      fontFamily: "var(--font-primary)",
    };

    const decorativeElementStyles = {
      position: "absolute",
      top: "-20px",
      right: "-20px",
      width: "100px",
      height: "100px",
      borderRadius: "50%",
      background: `radial-gradient(circle, ${colorVariant}15, transparent 70%)`,
      pointerEvents: "none",
    };

    const badgeStyles = {
      position: "absolute",
      top: "var(--spacing-xl)",
      right: "var(--spacing-xl)",
      padding: "6px 12px",
      borderRadius: "16px",
      background: `${colorVariant}15`,
      color: colorVariant,
      fontSize: "11px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      fontFamily: "var(--font-primary)",
    };

    const getBadgeText = (color) => {
      const badges = {
        teal: "Yönetim",
        orange: "Odaklanma",
        purple: "Rutin",
        blue: "Analiz",
      };
      return badges[color] || "Özellik";
    };

    return (
      <TiltCard
        variant="feature"
        style={cardStyles}
        colorOverlay={colorVariant}
        tiltIntensity={8}
      >
        <div style={decorativeElementStyles} />

        <div style={iconContainerStyles}>
          <div style={iconStyles}>{feature.icon}</div>
          <div
            style={{
              position: "absolute",
              inset: "0",
              borderRadius: "24px",
              background:
                "linear-gradient(45deg, rgba(255,255,255,0.3), transparent)",
              pointerEvents: "none",
            }}
          />
        </div>

        <h3 style={featureTitleStyles}>{feature.title}</h3>
        <p style={featureDescriptionStyles}>{feature.description}</p>
      </TiltCard>
    );
  };

  return (
    <section style={sectionStyles} id="features" ref={sectionRef}>
      {/* Alt geçiş katmanı */}
      <div
        style={{
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          height: "80px",
          background:
            "linear-gradient(180deg, transparent 0%, rgba(240, 253, 250, 0.2) 70%, rgba(240, 253, 250, 0.4) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div style={containerStyles}>
        <div style={headerStyles}>
          <h2 style={titleStyles}>Smart Inbox Management</h2>
          <p style={subtitleStyles}>
            Using advanced AI, Inbox Zero offers smart inbox cleanup, email
            writing automation, and priority sorting to help you gain control of
            your day.
          </p>
        </div>

        <div style={gridStyles}>
          {FeatureData.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
